import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { api } from '../api';
import {
  Plus,
  FolderKanban,
  Calendar,
  MapPin,
  Building2,
  ChevronRight,
  CheckCircle2,
  Circle,
  X,
  Link as LinkIcon,
  RefreshCw,
  Download,
  Wifi,
  WifiOff,
  Webhook,
  History,
} from 'lucide-react';
import { toast } from 'sonner';

function NewProjectDialog({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [location, setLocation] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');

  if (!open) return null;

  const reset = () => {
    setName('');
    setClient('');
    setLocation('');
    setStartDate('');
    setEndDate('');
    setNotes('');
  };

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Project name is required');
    const dates = [];
    if (startDate) dates.push(startDate);
    if (endDate && endDate !== startDate) dates.push(endDate);
    onCreate({
      name: name.trim(),
      client: client.trim(),
      location: location.trim(),
      shootDates: dates,
      notes: notes.trim(),
    });
    reset();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="surface rounded-lg w-full max-w-lg overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
          <h2 className="text-base font-semibold text-white">New Project</h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
              Project Name *
            </label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Corp Brand Video"
              className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                Client
              </label>
              <input
                value={client}
                onChange={(e) => setClient(e.target.value)}
                placeholder="Acme Corporation"
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                Location
              </label>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Dallas, TX"
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
              Notes
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any details about the shoot…"
              className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 resize-none"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-900 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900"
          >
            Cancel
          </button>
          <button type="submit" className="btn-amber text-xs px-4 py-1.5 rounded-md">
            Create Project
          </button>
        </div>
      </form>
    </div>
  );
}

function ProjectCard({ project }) {
  const { projectProgress } = useApp();
  const navigate = useNavigate();
  const { totalUnits, packedUnits } = projectProgress(project);
  const pct = totalUnits === 0 ? 0 : Math.round((packedUnits / totalUnits) * 100);
  const isCompleted = project.status === 'completed';

  return (
    <div className="surface rounded-lg p-5 text-left hover:border-amber-500/40 transition-colors group block relative">
      <button
        onClick={() => navigate(`/projects/${project.id}`)}
        className="w-full text-left"
      >
        <div className="flex items-start justify-between mb-3 pr-8">
          <div className="flex items-center gap-2 flex-wrap">
            {isCompleted ? (
              <CheckCircle2 size={14} className="text-emerald-400" />
            ) : (
              <Circle size={14} className="text-amber-400" />
            )}
            <span
              className={`text-[10px] tracking-[0.2em] uppercase font-medium ${
                isCompleted ? 'text-emerald-400' : 'text-amber-400'
              }`}
            >
              {isCompleted ? 'Completed' : 'Active'}
            </span>
            {project.externalLinkBroken && (
              <span
                className="text-[9px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-300"
                title="The upstream PM project no longer exists. The gear project and its packing history are unchanged."
                data-testid="pm-link-broken-badge"
              >
                PM link broken
              </span>
            )}
            {!project.externalLinkBroken && project.dayCount > 0 && (
              <span
                className="text-[9px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-neutral-800 border border-neutral-700 text-neutral-300"
                title={`${project.dayCount} shoot day${project.dayCount === 1 ? '' : 's'} on the call sheet`}
                data-testid="day-count-badge"
              >
                {project.dayCount} {project.dayCount === 1 ? 'day' : 'days'}
              </span>
            )}
          </div>
          <ChevronRight size={16} className="text-neutral-600 group-hover:text-amber-400 transition-colors" />
        </div>

        <h3 className="text-base font-semibold text-white mb-1 leading-snug">{project.name}</h3>

        <div className="space-y-1.5 mb-4">
          {project.client && (
            <div className="flex items-center gap-2 text-xs text-neutral-400">
              <Building2 size={12} className="text-neutral-600" /> {project.client}
            </div>
          )}
          {project.location && (
            <div className="flex items-center gap-2 text-xs text-neutral-400">
              <MapPin size={12} className="text-neutral-600" /> {project.location}
            </div>
          )}
          {project.shootDates?.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-neutral-400">
              <Calendar size={12} className="text-neutral-600" />
              {project.shootDates.join(' → ')}
            </div>
          )}
        </div>

        {!isCompleted && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] tracking-wider uppercase text-neutral-500">Packing progress</span>
              <span className="text-[11px] text-neutral-300 font-medium">
                {packedUnits} / {totalUnits}
              </span>
            </div>
            <div className="h-1.5 bg-neutral-900 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
        {isCompleted && (
          <div className="text-[11px] text-neutral-500">All gear returned.</div>
        )}
      </button>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

function PMSyncSection() {
  const navigate = useNavigate();
  const [pmProjects, setPmProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [open, setOpen] = useState(false);
  const [syncedAt, setSyncedAt] = useState(null);
  const [urlHost, setUrlHost] = useState('');
  const [testing, setTesting] = useState(false);
  const [health, setHealth] = useState(null);
  const [whTesting, setWhTesting] = useState(false);
  const [whDialogOpen, setWhDialogOpen] = useState(false);
  const [whUrl, setWhUrl] = useState('');
  const [whResult, setWhResult] = useState(null);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [, forceTick] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.pmListProjects();
      setPmProjects(r.projects || []);
      setSyncedAt(r.syncedAt || new Date().toISOString());
      if (r.urlHost) setUrlHost(r.urlHost);
    } catch (e) {
      toast.error(e.message || 'Could not reach Project Manager');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const h = await api.pmHealth();
      setHealth(h);
      if (h.urlHost) setUrlHost(h.urlHost);
      if (h.ok) {
        toast.success(
          `Connected to ${h.urlHost || 'PM'} · ${h.projectCount ?? '?'} projects · ${h.latencyMs}ms`,
        );
      } else {
        toast.error(
          `PM unreachable (${h.statusCode || 'no response'}): ${h.error || 'unknown'}`,
        );
      }
    } catch (e) {
      toast.error(e.message || 'Health check failed');
    } finally {
      setTesting(false);
    }
  };

  const openWebhookEvents = async () => {
    setEventsOpen(true);
    setEventsLoading(true);
    try {
      const r = await api.pmWebhookEvents(50);
      setEvents(r.events || []);
    } catch (e) {
      toast.error(e.message || 'Could not load webhook events');
    } finally {
      setEventsLoading(false);
    }
  };

  const openWebhookTest = () => {
    // Default the URL to the configured backend's own webhook endpoint so the
    // user can either hit "Test" immediately (smoke-test current deploy) or
    // paste a new production URL after deploying.
    const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    setWhUrl(backend ? `${backend}/api/webhooks/whiplash-pm` : '');
    setWhResult(null);
    setWhDialogOpen(true);
  };

  const runWebhookTest = async () => {
    setWhTesting(true);
    setWhResult(null);
    try {
      const r = await api.pmWebhookTest(whUrl);
      setWhResult(r);
      if (r.ok) {
        toast.success(
          `Webhook reachable · HMAC verified (${r.statusCode} in ${r.latencyMs}ms)`,
        );
      } else if (r.stage === 'hmac') {
        toast.error('Signature rejected (401). The shared secret does not match.');
      } else if (r.stage === 'config') {
        toast.error('Receiver returned 503 — WHIPLASH_PM_WEBHOOK_SECRET not set on target.');
      } else if (r.stage === 'secret') {
        toast.error('Cannot sign: WHIPLASH_PM_WEBHOOK_SECRET is unset on this server.');
      } else {
        toast.error(`Webhook unreachable: ${r.error || `HTTP ${r.statusCode}`}`);
      }
    } catch (e) {
      toast.error(e.message || 'Webhook test failed');
    } finally {
      setWhTesting(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Re-render every 30s so "last synced" relative time stays fresh
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const unlinked = pmProjects.filter((p) => !p.linkedLocalId);
  if (pmProjects.length === 0 && !loading && !health) return null;

  const healthDotClass = health
    ? health.ok
      ? 'bg-emerald-400'
      : 'bg-rose-500'
    : 'bg-neutral-600';

  return (
    <div className="surface rounded-lg p-4 mb-4 border-amber-500/20" data-testid="pm-sync-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <LinkIcon size={14} className="text-amber-400" />
          <span className="text-sm font-semibold text-white">Project Manager</span>
          <span className="text-[11px] text-neutral-500 truncate">
            {loading
              ? 'Checking…'
              : `${pmProjects.length} found · ${unlinked.length} not yet linked`}
          </span>
          {urlHost && (
            <span
              className="hidden sm:inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-neutral-800 text-neutral-400"
              title={`Source: ${urlHost}`}
              data-testid="pm-host-badge"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${healthDotClass}`} />
              {urlHost}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {syncedAt && (
            <span
              className="text-[10px] text-neutral-500"
              title={new Date(syncedAt).toLocaleString()}
              data-testid="pm-last-synced"
            >
              Last synced {timeAgo(syncedAt)}
            </span>
          )}
          <button
            onClick={testConnection}
            disabled={testing}
            className="text-[11px] px-2 py-1 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900 flex items-center gap-1 disabled:opacity-60"
            data-testid="pm-test-connection-btn"
          >
            {health && !health.ok ? (
              <WifiOff size={11} className="text-rose-400" />
            ) : (
              <Wifi size={11} className={health?.ok ? 'text-emerald-400' : ''} />
            )}
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <button
            onClick={openWebhookTest}
            className="text-[11px] px-2 py-1 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900 flex items-center gap-1"
            data-testid="pm-test-webhook-btn"
            title="Send a signed test event to confirm the webhook URL + HMAC secret are wired up"
          >
            <Webhook size={11} /> Test webhook
          </button>
          <button
            onClick={openWebhookEvents}
            className="text-[11px] px-2 py-1 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900 flex items-center gap-1"
            data-testid="pm-webhook-events-btn"
            title="See what PM has sent us recently — status, project id, event type"
          >
            <History size={11} /> Deliveries
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="text-[11px] px-2 py-1 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900 flex items-center gap-1 disabled:opacity-60"
            data-testid="pm-refresh-btn"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          {pmProjects.length > 0 && (
            <button
              onClick={() => setOpen((v) => !v)}
              className="text-[11px] px-2 py-1 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900"
              data-testid="pm-toggle-list-btn"
            >
              {open ? 'Hide' : 'Show'} projects
            </button>
          )}
        </div>
      </div>

      {health && !health.ok && (
        <div className="mt-2 text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded px-2 py-1.5">
          <span className="font-semibold">Connection failed</span>
          {health.statusCode ? ` · HTTP ${health.statusCode}` : ''} ·{' '}
          {health.error || 'No response from Project Manager'}
        </div>
      )}

      {eventsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setEventsOpen(false)}
          data-testid="pm-webhook-events-dialog"
        >
          <div
            className="surface rounded-lg w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
              <div className="flex items-center gap-2">
                <History size={14} className="text-amber-400" />
                <h2 className="text-sm font-semibold text-white">Recent PM Deliveries</h2>
                <span className="text-[10px] text-neutral-500">
                  {eventsLoading ? 'Loading…' : `${events.length} shown`}
                </span>
              </div>
              <button
                onClick={() => setEventsOpen(false)}
                className="text-neutral-500 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
            <div className="overflow-auto">
              {!eventsLoading && events.length === 0 ? (
                <div className="p-6 text-center text-xs text-neutral-500">
                  No deliveries recorded yet.
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-neutral-950 text-neutral-500 uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2">Received</th>
                      <th className="text-left px-4 py-2">Event</th>
                      <th className="text-left px-4 py-2">PM project</th>
                      <th className="text-left px-4 py-2">Status sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.eventId} className="border-t border-neutral-900 text-neutral-300">
                        <td className="px-4 py-2 font-mono text-[11px] whitespace-nowrap">
                          {e.receivedAt ? new Date(e.receivedAt).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-2 font-mono text-[11px] text-amber-300">
                          {e.eventType}
                        </td>
                        <td className="px-4 py-2">
                          <div className="text-white truncate max-w-[220px]">
                            {e.pmProjectName || '—'}
                          </div>
                          <div className="text-[10px] text-neutral-500 font-mono truncate max-w-[220px]">
                            {e.pmProjectId || ''}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          {e.pmStatusSent ? (
                            <span className="font-mono text-emerald-300">
                              {e.pmStatusSent}
                            </span>
                          ) : (
                            <span className="text-neutral-600">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {whDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => !whTesting && setWhDialogOpen(false)}
          data-testid="webhook-test-dialog"
        >
          <div
            className="surface rounded-lg w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
              <div className="flex items-center gap-2">
                <Webhook size={14} className="text-amber-400" />
                <h2 className="text-sm font-semibold text-white">Test webhook signature</h2>
              </div>
              <button
                type="button"
                onClick={() => setWhDialogOpen(false)}
                disabled={whTesting}
                className="text-neutral-500 hover:text-white disabled:opacity-60"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-[11px] text-neutral-400 leading-relaxed">
                Posts a signed <span className="font-mono text-neutral-200">webhook.test</span>{' '}
                event to the URL below using the configured{' '}
                <span className="font-mono text-neutral-200">WHIPLASH_PM_WEBHOOK_SECRET</span>.
                Use it after deploying to confirm PM can reach the receiver and the shared
                secret matches.
              </p>
              <div>
                <label className="block text-[10px] tracking-wider uppercase text-neutral-400 mb-1.5">
                  Webhook URL
                </label>
                <input
                  value={whUrl}
                  onChange={(e) => setWhUrl(e.target.value)}
                  placeholder="https://your-deployed-host/api/webhooks/whiplash-pm"
                  className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/60"
                  data-testid="webhook-test-url-input"
                />
                <p className="text-[10px] text-neutral-500 mt-1">
                  Defaults to this app&apos;s own receiver. Paste your production URL to
                  smoke-test a fresh deploy.
                </p>
              </div>

              {whResult && (
                <div
                  className={`text-[11px] rounded border px-3 py-2 space-y-1 ${
                    whResult.ok
                      ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-200'
                      : 'border-rose-500/30 bg-rose-500/5 text-rose-200'
                  }`}
                  data-testid="webhook-test-result"
                >
                  <div className="font-semibold">
                    {whResult.ok ? 'Success' : 'Failed'} · stage:{' '}
                    <span className="font-mono">{whResult.stage}</span>
                  </div>
                  {whResult.statusCode != null && (
                    <div>
                      HTTP <span className="font-mono">{whResult.statusCode}</span>
                      {whResult.latencyMs != null && (
                        <span className="text-neutral-500"> · {whResult.latencyMs}ms</span>
                      )}
                    </div>
                  )}
                  {whResult.error && (
                    <div className="font-mono text-[10px] break-all">{whResult.error}</div>
                  )}
                  {whResult.response && (
                    <div className="font-mono text-[10px] text-neutral-400 break-all">
                      {typeof whResult.response === 'string'
                        ? whResult.response
                        : JSON.stringify(whResult.response)}
                    </div>
                  )}
                  {!whResult.ok && whResult.stage === 'hmac' && (
                    <div className="text-[10px] text-rose-300">
                      The receiver accepted the request but the signature didn&apos;t verify.
                      Confirm both sides use the same{' '}
                      <span className="font-mono">WHIPLASH_PM_WEBHOOK_SECRET</span>.
                    </div>
                  )}
                  {!whResult.ok && whResult.stage === 'config' && (
                    <div className="text-[10px] text-rose-300">
                      The receiver is alive but doesn&apos;t have a secret configured. Set{' '}
                      <span className="font-mono">WHIPLASH_PM_WEBHOOK_SECRET</span> on the
                      deployed backend and redeploy.
                    </div>
                  )}
                  {!whResult.ok && whResult.stage === 'transport' && (
                    <div className="text-[10px] text-rose-300">
                      Could not reach the URL. Double-check the host, SSL cert, and that the
                      backend is up.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-neutral-900 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setWhDialogOpen(false)}
                disabled={whTesting}
                className="text-xs px-3 py-1.5 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900 disabled:opacity-60"
              >
                Close
              </button>
              <button
                type="button"
                onClick={runWebhookTest}
                disabled={whTesting || !whUrl.trim()}
                className="btn-amber text-xs px-4 py-1.5 rounded-md disabled:opacity-60 inline-flex items-center gap-1.5"
                data-testid="webhook-test-run-btn"
              >
                <Webhook size={11} />
                {whTesting ? 'Sending…' : 'Send test event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="mt-3 space-y-1.5">
          {pmProjects.map((p) => {
            const linked = !!p.linkedLocalId;
            return (
              <div
                key={p.id}
                className="flex items-center gap-3 px-3 py-2 rounded-md border border-neutral-900 bg-neutral-950"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{p.name}</div>
                  <div className="text-[11px] text-neutral-500 flex items-center gap-2 flex-wrap">
                    {p.director && <span>Dir: {p.director}</span>}
                    {p.location && <span>{p.location}</span>}
                    {p.shootDates?.length > 0 && <span>{p.shootDates.join(' → ')}</span>}
                    {p.jobNumber && <span className="font-mono">#{p.jobNumber}</span>}
                  </div>
                </div>
                {linked ? (
                  <button
                    onClick={() => navigate(`/projects/${p.linkedLocalId}`)}
                    className="text-[11px] px-2 py-1 rounded border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  >
                    Linked ✓
                  </button>
                ) : (
                  <button
                    disabled={importing === p.id}
                    onClick={async () => {
                      setImporting(p.id);
                      try {
                        const newP = await api.pmImportProject(p.id);
                        toast.success(`Imported "${newP.name}"`);
                        await load();
                        navigate(`/projects/${newP.id}`);
                      } catch (e) {
                        toast.error(e.message || 'Import failed');
                      } finally {
                        setImporting(null);
                      }
                    }}
                    className="btn-amber text-[11px] px-2.5 py-1 rounded-md flex items-center gap-1 disabled:opacity-60"
                  >
                    <Download size={11} /> {importing === p.id ? 'Importing…' : 'Import'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Projects() {
  const { projects, createProject } = useApp();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('active');

  const active = projects.filter((p) => p.status === 'active');
  const completed = projects.filter((p) => p.status === 'completed');
  const visible = tab === 'active' ? active : completed;

  const handleCreate = async (data) => {
    try {
      const p = await createProject(data);
      toast.success(`Project "${p.name}" created`);
    } catch (e) {
      toast.error(e.message || 'Could not create project');
    }
  };

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <FolderKanban size={20} className="text-amber-400" /> Projects
          </h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            Plan, pack, and track gear for each shoot.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="btn-amber rounded-md px-3 py-1.5 text-xs flex items-center gap-2"
        >
          <Plus size={14} /> New Project
        </button>
      </div>

      <PMSyncSection />

      <div className="flex items-center gap-1 mb-4 border-b border-neutral-900">
        <button
          onClick={() => setTab('active')}
          className={`px-3 py-2 text-xs border-b-2 -mb-px transition-colors ${
            tab === 'active'
              ? 'border-amber-500 text-amber-400'
              : 'border-transparent text-neutral-500 hover:text-neutral-300'
          }`}
        >
          Active ({active.length})
        </button>
        <button
          onClick={() => setTab('completed')}
          className={`px-3 py-2 text-xs border-b-2 -mb-px transition-colors ${
            tab === 'completed'
              ? 'border-amber-500 text-amber-400'
              : 'border-transparent text-neutral-500 hover:text-neutral-300'
          }`}
        >
          Completed ({completed.length})
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="surface rounded-lg p-12 text-center">
          <FolderKanban size={32} className="mx-auto text-neutral-700 mb-3" />
          <p className="text-sm text-neutral-400 mb-4">
            {tab === 'active' ? 'No active projects yet.' : 'No completed projects yet.'}
          </p>
          {tab === 'active' && (
            <button
              onClick={() => setOpen(true)}
              className="btn-amber rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-2"
            >
              <Plus size={13} /> Create your first project
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}

      <NewProjectDialog open={open} onClose={() => setOpen(false)} onCreate={handleCreate} />
    </div>
  );
}
